<template>
  <div class="flex flex-wrap gap-4">
    <div v-for="badge in visibleBadges" :key="badge.key" @click="handleBadgeClick(badge.key)">
      <component
        :is="badge.graphic"
        :active="badge.active"
        :width="badgeSize"
        :height="badgeSize"
        :class="`sitter-badge ${badge.key}`"
      />
    </div>
  </div>
</template>

<script lang="ts">
import IDVBadge from '../../graphics/badges/IDVBadge.vue';
import BackgroundCheckBadge from '../../graphics/badges/BackgroundCheckBadge.vue';
import EnhancedBackgroundCheckBadge from '../../graphics/badges/EnhancedBackgroundCheckBadge.vue';
import MotorVehicleRecordBadge from '../../graphics/badges/MotorVehicleRecordBadge.vue';
import CPRbadge from '../../graphics/badges/CPRBadge.vue';
import FirstAidBadge from '../../graphics/badges/FirstAidBadge.vue';

interface Badge {
  completedAt: DateTime;
  expiresAt: DateTime;
  name: string;
  status: string;
}

interface IDV {
  latestUpdate: DateTime;
  provider: string;
  status: string;
  verdict: string;
}

export default {
  name: 'SitterBadgeList',
  props: {
    badges: {
      type: Array as () => Badge[],
      default: () => [],
    },
    idv: {
      type: Object as () => IDV,
      default: () => ({}),
    },
    badgeSize: {
      type: Number,
      default: 60,
    },
    includeInactiveBadges: {
      type: Boolean,
      default: true,
    },
    checksOnly: {
      type: Boolean,
      default: false,
    },
    certificationsOnly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    identityVerificationPassed(): boolean {
      return this.idv?.verdict && this.idv?.verdict === 'passed';
    },
    badgeStatuses() {
      const checkBadges = {
        basicCriminal: this.checkBadge('basic_criminal'),
        enhancedCriminal: this.checkBadge('enhanced_criminal'),
        motorVehicleRecord: this.checkBadge('motor_vehicle'),
      };

      // Only include idv badge if IDV passed
      if (this.identityVerificationPassed) checkBadges.idv = true;

      const certificationBadges = {
        cpr: this.checkBadge('cpr_certified'),
        firstAid: this.checkBadge('first_aid_training'),
      };

      if (this.checksOnly) return checkBadges;
      if (this.certificationsOnly) return certificationBadges;

      return { ...checkBadges, ...certificationBadges };
    },
    badgesList() {
      const badgeConfig = [
        { key: 'idv', graphic: IDVBadge },
        { key: 'basicCriminal', graphic: BackgroundCheckBadge },
        { key: 'enhancedCriminal', graphic: EnhancedBackgroundCheckBadge },
        { key: 'motorVehicleRecord', graphic: MotorVehicleRecordBadge },
        { key: 'cpr', graphic: CPRbadge },
        { key: 'firstAid', graphic: FirstAidBadge },
      ];

      return badgeConfig
        .filter((badge) => this.badgeStatuses[badge.key] !== undefined)
        .map((badge) => ({
          key: badge.key,
          graphic: badge.graphic,
          active: !!this.badgeStatuses[badge.key],
          showGraphic: this.includeInactiveBadges || !!this.badgeStatuses[badge.key],
        }));
    },
    visibleBadges() {
      return this.badgesList.filter((badge) => badge.showGraphic);
    },
  },
  methods: {
    checkBadge(badgeName: string): boolean {
      return this.badges?.some((badge: Badge) => badge.name === badgeName && badge.status === 'COMPLETE');
    },
    handleBadgeClick(badgeKey: string) {
      if (badgeKey === 'cpr' || badgeKey === 'firstAid') {
        this.$emit('certification-click', badgeKey);
      } else {
        this.$emit('check-click', badgeKey);
      }
    },
  },
};
</script>
